import React from 'react';
import {ValueCell} from '../../../../../../components/initial-plan/ValueCell';
import {HIDDEN_FEATURES} from '../../../../../../constants';
import ForecastTooltip from '../../../../../shared/components/tooltips/ForecastTooltip';
import ProjectUtil from '../../../../../shared/util/ProjectUtil';
import {Data, HorizontalBar, Title, ValueContainer} from '../ProjectScopingTotals_styled';
import {RemainingValueTooltip, VarianceValueTooltip} from '../ProjectScopingTotalsTooltips';
import {AvailabilityTile} from './AvailabilityTile';
import {Tile, TileRow} from './Tile_styled';
import {isFeatureHidden} from '../../../../../shared/util/CompanySetupUtil';
import {useIntl} from 'react-intl';
import {useProjectTotals} from '../ProjectScopingTotalsContext';

interface RemainingAndBaselineTileProps {
	showRevenue: boolean;
	showBaseline: boolean;
	showAvailabilityTile: boolean;
}
const RemainingRow = () => {
	const intl = useIntl();
	const {totalsData} = useProjectTotals();
	return (
		<TileRow>
			<Title>
				{totalsData.isEstimatedInHours
					? intl.formatMessage({id: 'project_scoping.forecast_hours_remaining'})
					: 'Forecast points remaining'}
			</Title>
			<Data small>
				<ForecastTooltip placement={'left'} content={RemainingValueTooltip()}>
					<ValueCell
						noJustify
						type={totalsData.isEstimatedInHours ? ValueCell.VALUE_TYPE.TIME : ValueCell.VALUE_TYPE.POINTS}
						value={totalsData.remaining}
					/>
				</ForecastTooltip>
			</Data>
		</TileRow>
	);
};

const VarianceWithEstimateRow = () => {
	const intl = useIntl();
	const {totalsData, projectData} = useProjectTotals();
	if (!ProjectUtil.projectUsesManualProgress(projectData) && !isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS)) {
		return (
			<TileRow>
				<Title>{intl.formatMessage({id: 'project_scoping.variance_estimate'})}</Title>
				<Data small>
					<ForecastTooltip placement={'left'} content={VarianceValueTooltip()}>
						<ValueContainer>
							<ValueCell noJustify type={ValueCell.VALUE_TYPE.TIME} value={totalsData.difference} />
						</ValueContainer>
					</ForecastTooltip>
				</Data>
			</TileRow>
		);
	}

	return null;
};

const BaselineEstimateRow = () => {
	const intl = useIntl();
	const {totalsData} = useProjectTotals();
	return (
		<TileRow>
			<Title>{intl.formatMessage({id: 'project_scoping.baseline_estimate'})}</Title>
			<Data small>
				<ValueCell noJustify type={ValueCell.VALUE_TYPE.TIME} value={totalsData.baselineTargetHours} />
			</Data>
		</TileRow>
	);
};

const BaselineRevenueRow = ({showRevenue}: Pick<RemainingAndBaselineTileProps, 'showRevenue'>) => {
	const intl = useIntl();
	const {totalsData, settings} = useProjectTotals();
	if (showRevenue) {
		return (
			<TileRow>
				<Title>{intl.formatMessage({id: 'baseline.baseline_billable_time'})}</Title>
				<Data small>
					<ValueCell
						noJustify
						type={ValueCell.VALUE_TYPE.PRICE}
						value={totalsData.baselineTargetPrice}
						currencySymbol={settings.currencySymbol}
					/>
				</Data>
			</TileRow>
		);
	}

	return null;
};

export const RemainingAndBaselineTile: React.FC<RemainingAndBaselineTileProps> = ({
	showRevenue,
	showBaseline,
	showAvailabilityTile,
}) => {
	return (
		<Tile>
			<RemainingRow />
			<VarianceWithEstimateRow />
			{showBaseline ? (
				<>
					<HorizontalBar />
					<BaselineEstimateRow />
					<BaselineRevenueRow showRevenue={showRevenue} />
				</>
			) : showAvailabilityTile ? (
				<>
					<HorizontalBar />
					<AvailabilityTile />
				</>
			) : null}
		</Tile>
	);
};
